import { useState, type FormEvent, type ReactNode } from 'react';
import { ExternalLink, Trash2 } from 'lucide-react';
import { canRemoveRumContribution, createRumLinkSchema, type RumLinkView } from '@cercle/shared';
import { useApi, useApp } from '@/shared/app-context';
import { useAsync } from '@/shared/hooks/useAsync';
import { useOnlineStatus } from '@/shared/hooks/useOnlineStatus';
import { errorMessage } from '@/shared/http/http-client';
import { Button, ErrorText, Field, Input, Muted, Skeleton } from '@/shared/ui';
import { formatShortDate } from '../session/format';
import styles from './rum-contributions.module.scss';

/**
 * Les liens utiles d'une fiche : un test, une boutique, la page de la distillerie.
 *
 * Ouvert à tous et à tout moment — on retombe sur un article six mois après la dégustation, et
 * c'est justement là qu'on veut pouvoir l'accrocher. Seuls son auteur et la modération
 * retirent une ligne ; rien ne se renomme, personne ne réécrit l'apport d'un autre.
 *
 * Sa propre section, avec son propre chargement : une fiche dont les liens échouent doit
 * rester lisible. C'est déjà le découpage des autres blocs de l'écran.
 */
export function RumLinks({ rumId }: { rumId: string }): ReactNode {
  const api = useApi();
  const { member } = useApp();
  const online = useOnlineStatus();
  const links = useAsync(() => api.rums.links(rumId), [rumId]);

  const [url, setUrl] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function add(event: FormEvent): Promise<void> {
    event.preventDefault();

    // Le même schéma que celui dont l'API se sert : un `javascript:` est refusé ici avant
    // d'être refusé là-bas, avec le même message.
    const parsed = createRumLinkSchema.safeParse({ url, label: label.trim() || null });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Lien invalide.');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await api.rums.addLink(rumId, parsed.data);
      setUrl('');
      setLabel('');
      links.reload();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  async function remove(linkId: string): Promise<void> {
    setError(null);
    try {
      await api.rums.removeLink(rumId, linkId);
      links.reload();
    } catch (cause) {
      setError(errorMessage(cause));
    }
  }

  return (
    <section>
      <h2 className={styles.sectionTitle}>Liens utiles</h2>

      {links.loading ? <Skeleton lines={2} /> : null}
      {links.error ? <ErrorText>{links.error}</ErrorText> : null}

      {links.data && links.data.length > 0 ? (
        <ul className={styles.list}>
          {links.data.map((link) => (
            <li key={link.id} className={styles.link}>
              <a href={link.url} target="_blank" rel="noopener noreferrer nofollow">
                <ExternalLink size={15} strokeWidth={1.75} aria-hidden="true" />
                {labelOf(link)}
              </a>
              <Muted>
                {link.addedBy ? `${link.addedBy.pseudo} · ` : ''}
                {formatShortDate(link.createdAt)}
              </Muted>
              {member && canRemoveRumContribution(member.role, member.id, link.addedBy?.id ?? null) ? (
                <button
                  type="button"
                  className={styles.remove}
                  onClick={() => void remove(link.id)}
                  aria-label={`Retirer le lien ${labelOf(link)}`}
                >
                  <Trash2 size={15} strokeWidth={1.75} aria-hidden="true" />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : links.loading ? null : (
        <Muted>Aucun lien pour l’instant. Le premier est peut-être le vôtre.</Muted>
      )}

      <form className={styles.form} onSubmit={(event) => void add(event)} noValidate>
        <Field label="Ajouter un lien">
          <Input
            type="url"
            inputMode="url"
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            placeholder="https://…"
          />
        </Field>
        <Field label="Titre" hint="Facultatif : à défaut, le nom du site.">
          <Input
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            placeholder="Le test de Rhum Attitude"
          />
        </Field>
        <Button variant="secondary" type="submit" disabled={busy || !url.trim()}>
          {busy ? 'Ajout…' : 'Ajouter'}
        </Button>
      </form>

      <ErrorText>{error}</ErrorText>
      {!online ? (
        <Muted>
          Hors-ligne : un lien s’ajoute une fois le réseau revenu. Seules les notes se
          saisissent sans connexion.
        </Muted>
      ) : null}
    </section>
  );
}

/**
 * Le nom du site à défaut de titre.
 *
 * Une URL de deux cents caractères en travers de l'écran n'apprend rien ; « rumx.com » situe.
 * Le `www.` est retiré, il ne distingue rien. L'analyse ne peut pas échouer — l'API n'accepte
 * que du http(s) — mais un lien plus ancien que cette règle ne doit pas casser l'affichage.
 */
function labelOf(link: RumLinkView): string {
  if (link.label) return link.label;
  try {
    return new URL(link.url).hostname.replace(/^www\./, '');
  } catch {
    return link.url;
  }
}
